// ─── Workshop Truck Availability Tracker ───
// VehicleMap – Leaflet map showing vehicle positions with moving/stationary filter
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useMemo, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import type { Truck } from '@/types'
import StatusBadge from '@/components/ui/StatusBadge'
import { normalizeMake } from '@/utils/helpers'

// ─── Fix Leaflet default icon (broken in bundlers) ──────────

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

// ─── Custom marker icons ────────────────────────────────────

function createIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: '',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    popupAnchor: [0, -8],
    html: `<div style="
      width:12px;height:12px;border-radius:50%;
      background:${color};
      border:2px solid white;
      box-shadow:0 1px 4px rgba(0,0,0,0.3);
    "></div>`,
  })
}

const ICON_MOVING = createIcon('#10B981')    // green
const ICON_STATIONARY = createIcon('#000000') // black
const ICON_WORKSHOP = createIcon('#F59E0B')   // amber

// ─── Filter type ────────────────────────────────────────────

type MapFilter = 'all' | 'moving' | 'stationary'

// ─── Auto-fit bounds component ──────────────────────────────

function FitBounds({ trucks }: { trucks: Truck[] }) {
  const map = useMap()
  useEffect(() => {
    if (trucks.length === 0) return
    const points = trucks
      .filter((t) => t.current_lat && t.current_lon)
      .map((t) => [t.current_lat!, t.current_lon!] as [number, number])
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 12 })
    }
  }, [trucks, map])
  return null
}

// ─── Props ──────────────────────────────────────────────────

interface VehicleMapProps {
  trucks: Truck[]
  onTruckClick?: (truck: Truck) => void
  height?: string
}

// ─── Component ──────────────────────────────────────────────

export default function VehicleMap({
  trucks,
  onTruckClick,
  height = '350px',
}: VehicleMapProps) {
  const [filter, setFilter] = useState<MapFilter>('all')
  const [fleetSearch, setFleetSearch] = useState('')

  // Only trucks with GPS coordinates
  const geoTrucks = useMemo(
    () => trucks.filter((t) => t.current_lat != null && t.current_lon != null),
    [trucks],
  )

  const filteredTrucks = useMemo(() => {
    let result = geoTrucks
    if (filter === 'moving') result = result.filter((t) => (t.speed_kmh ?? 0) > 0)
    if (filter === 'stationary') result = result.filter((t) => !t.speed_kmh || t.speed_kmh === 0)
    if (fleetSearch.trim()) {
      const q = fleetSearch.toLowerCase()
      result = result.filter((t) =>
        (t.registration ?? '').toLowerCase().includes(q) ||
        (t.fleet_number ?? '').toLowerCase().includes(q),
      )
    }
    return result
  }, [geoTrucks, filter, fleetSearch])

  const movingCount = useMemo(
    () => geoTrucks.filter((t) => (t.speed_kmh ?? 0) > 0).length,
    [geoTrucks],
  )
  const stationaryCount = geoTrucks.length - movingCount

  function getIcon(truck: Truck): L.DivIcon {
    if (truck.is_available === 0) return ICON_WORKSHOP
    if ((truck.speed_kmh ?? 0) > 0) return ICON_MOVING
    return ICON_STATIONARY
  }

  return (
    <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height }}>
      {/* Fleet # search overlaid on map */}
      <div className="absolute top-3 left-14 z-[1000] bg-white/95 backdrop-blur rounded-lg shadow-md p-1">
        <input
          type="text"
          value={fleetSearch}
          onChange={(e) => setFleetSearch(e.target.value)}
          placeholder="Filter fleet #…"
          className="w-36 px-2.5 py-1 text-xs border-none bg-transparent outline-none placeholder-gray-400"
        />
        {fleetSearch && (
          <button
            onClick={() => setFleetSearch('')}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
          >
            ✕
          </button>
        )}
      </div>

      {/* Filter controls overlaid on map */}
      <div className="absolute top-3 right-3 z-[1000] flex gap-1 bg-white/95 backdrop-blur rounded-lg shadow-md p-1">
        {([
          { value: 'all' as const, label: 'All', count: geoTrucks.length },
          { value: 'moving' as const, label: 'Moving', count: movingCount },
          { value: 'stationary' as const, label: 'Stopped', count: stationaryCount },
        ]).map(({ value, label, count }) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors
              ${filter === value
                ? 'bg-[#1E2A3A] text-white'
                : 'text-gray-600 hover:bg-gray-100'
              }`}
          >
            {label} <span className="opacity-70">({count})</span>
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-[1000] bg-white/95 backdrop-blur rounded-lg shadow-md px-3 py-2 text-[10px] flex gap-3">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#10B981]" /> Moving
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#000000]" /> Stopped
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Workshop
        </span>
      </div>

      {geoTrucks.length === 0 ? (
        <div className="flex items-center justify-center h-full bg-gray-50 text-gray-400 text-sm">
          No GPS data available
        </div>
      ) : (
        <MapContainer
          center={[9.0820, 8.6753]}
          zoom={6}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds trucks={filteredTrucks} />
          {filteredTrucks.map((truck) => (
            <Marker
              key={truck.asset_id}
              position={[truck.current_lat!, truck.current_lon!]}
              icon={getIcon(truck)}
              eventHandlers={{
                click: () => onTruckClick?.(truck),
              }}
            >
              <Popup>
                <div className="text-xs min-w-[180px]">
                  <p className="font-bold text-sm text-gray-800 mb-1">{truck.registration}</p>
                  <div className="space-y-0.5 text-gray-600">
                    <p><span className="font-medium">Client:</span> {truck.client_name}</p>
                    {truck.fleet_number && <p><span className="font-medium">Fleet #:</span> {truck.fleet_number}</p>}
                    {truck.make && <p><span className="font-medium">Vehicle:</span> {[normalizeMake(truck.make), truck.model].filter(Boolean).join(' ')}</p>}
                    <p><span className="font-medium">Speed:</span> {truck.speed_kmh != null ? `${truck.speed_kmh} km/h` : '—'}</p>
                    {truck.current_address && <p><span className="font-medium">Location:</span> {truck.current_address}</p>}
                  </div>
                  <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                    <StatusBadge status={truck.workshop_status} size="sm" />
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}
    </div>
  )
}
